import { useEffect, useState } from 'react'
import { useTranslation } from '../../i18n/useTranslation'
import { usePopup } from '../../hooks/usePopup'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { userApi } from '../../api'
import type { UserPreferences, UpdateUserPreferencesRequest } from '../../types/api'
import { Card, SettingsToggleRow } from '../ui'

export default function NotificationsSettings() {
  const { t } = useTranslation()
  const theme = useTheme()
  const { showSuccess, showError } = usePopup()
  const { refreshPreferences } = useUserPreferences()
  const { user } = useAuth()

  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const isCoach = user?.hasTrainees ?? false

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const data = await userApi.getUserPreferences()
        if (!cancelled) setPreferences(data)
      } catch (error) {
        console.error('Error loading preferences:', error)
        if (!cancelled) showError(t.common.errors.networkError)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
    // Load once on mount; avoid refetch loop when context updates after toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleToggle(
    field: keyof Pick<UserPreferences, 'notifyMyLastTrainingLeft' | 'notifyTraineesLastTrainingLeft' | 'notifyTrainingsCanBeCompleted'>,
    value: boolean
  ) {
    if (preferences == null) return
    try {
      setSaving(true)
      const request: UpdateUserPreferencesRequest = { [field]: value }
      const updated = await userApi.updateUserPreferences(request)
      setPreferences(updated)
      await refreshPreferences()
      showSuccess(t.preferences.saveSuccess)
    } catch (error) {
      console.error('Error saving notification preference:', error)
      showError(t.preferences.saveError)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-5">
        <div className="text-center py-8">{t.common.loading}</div>
      </div>
    )
  }

  const prefs = preferences ?? {}

  return (
    <div className="p-5">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1">{t.preferences.notifications.title}</h1>
        <p className="text-sm opacity-80">{t.preferences.notifications.description}</p>
      </header>

      <section className="mb-7">
        <h2
          className="text-xs font-bold uppercase tracking-widest mb-3"
          style={{ color: theme.text.muted }}
        >
          {t.preferences.notifications.traineeSection}
        </h2>
        <Card className="p-0 overflow-hidden">
          <div className="px-4">
            <SettingsToggleRow
              label={t.preferences.notifications.myLastTrainingLeft}
              checked={prefs.notifyMyLastTrainingLeft ?? true}
              onChange={(v) => handleToggle('notifyMyLastTrainingLeft', v)}
              disabled={saving}
              last
            />
          </div>
        </Card>
      </section>

      {isCoach && (
        <section className="mb-7">
          <h2
            className="text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: theme.text.muted }}
          >
            {t.preferences.notifications.coachSection}
          </h2>
          <Card className="p-0 overflow-hidden">
            <div className="px-4">
              <SettingsToggleRow
                label={t.preferences.notifications.traineesLastTrainingLeft}
                checked={prefs.notifyTraineesLastTrainingLeft ?? true}
                onChange={(v) => handleToggle('notifyTraineesLastTrainingLeft', v)}
                disabled={saving}
                last={false}
              />
              <SettingsToggleRow
                label={t.preferences.notifications.trainingsCanBeCompleted}
                checked={prefs.notifyTrainingsCanBeCompleted ?? true}
                onChange={(v) => handleToggle('notifyTrainingsCanBeCompleted', v)}
                disabled={saving}
                last
              />
            </div>
          </Card>
        </section>
      )}
    </div>
  )
}
